import { Alert, AlertIcon, Button, HStack, ListItem, Spinner, Stack, Text, UnorderedList } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { type AdmittedSupplierDto, useGetQualificationSchemeInviteesQuery } from "../../../../../../autogen/bff-api";

const names = (suppliers: AdmittedSupplierDto[]) => suppliers.map((supplier) => supplier.name).join(", ");

export const useQualificationSchemeInvitees = (eventId: string) =>
  useGetQualificationSchemeInviteesQuery({ eventId }, { refetchOnMountOrArgChange: true });

export const QualificationSchemeInvitees = ({ eventId }: { eventId: string }) => {
  const { t } = useTranslation();
  const { data, isFetching, isError, refetch } = useQualificationSchemeInvitees(eventId);

  if (isFetching) {
    return (
      <HStack spacing="2">
        <Spinner size="sm" color="smPrimary" />
        <Text fontSize="sm" color="smMuted">
          {t("Loading")}
        </Text>
      </HStack>
    );
  }
  if (isError || !data) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        <Stack spacing="2" align="flex-start">
          <Text fontSize="sm">{t("Could not load the suppliers to be invited.")}</Text>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            {t("Try again")}
          </Button>
        </Stack>
      </Alert>
    );
  }

  const noLongerAdmitted = data.noLongerAdmitted ?? [];
  const admittedSinceCreation = data.admittedSinceCreation ?? [];

  return (
    <Stack spacing="2" fontSize="sm">
      <Text fontWeight="semibold" color="smText">
        {data.frozen
          ? t("Invited suppliers ({{count}})", { count: data.invitees.length })
          : t("Invited when you open the competition ({{count}})", { count: data.invitees.length })}
      </Text>
      {data.invitees.length > 0 ? (
        <UnorderedList color="smText" spacing="0.5">
          {data.invitees.map((supplier) => (
            <ListItem key={supplier.organizationId}>{supplier.name}</ListItem>
          ))}
        </UnorderedList>
      ) : (
        <Text color="smMuted">
          {noLongerAdmitted.length > 0
            ? t(
                "None of the chosen suppliers is still qualified. Start a new competition from the qualification system to choose again.",
              )
            : t("No supplier is qualified in the system yet. Admit one under «Suppliers» on the qualification system.")}
        </Text>
      )}
      {noLongerAdmitted.length > 0 && (
        <Text color="smMuted">
          {t("No longer qualified in the system, so not invited: {{names}}", { names: names(noLongerAdmitted) })}
        </Text>
      )}
      {admittedSinceCreation.length > 0 && (
        <Text color="smMuted">
          {t("Qualified since the competition was created, so invited too: {{names}}", {
            names: names(admittedSinceCreation),
          })}
        </Text>
      )}
    </Stack>
  );
};
